import { useState, useMemo, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import type {
  ComponentHealth,
  ComponentCategory,
  EmailProviderName,
} from '@/services/api'
import type { EmailProviderSetting } from '@spoofio/shared/types'
import { Card, Button, Input, Badge, Select } from '@/components/ui'
import { EMAIL_PROVIDER_LABELS, EMAIL_PROVIDER_OPTIONS } from '@/constants/email-providers'
import { formatAuditTimestamp } from '@/utils/format-date'

// ============================================================================
// Status Helpers
// ============================================================================

function overallStatusVariant(status: string): 'success' | 'warning' | 'danger' {
  if (status === 'healthy') return 'success'
  if (status === 'degraded') return 'warning'
  return 'danger'
}

function componentStatusVariant(status: string): 'success' | 'warning' | 'danger' {
  if (status === 'healthy') return 'success'
  if (status === 'unconfigured') return 'warning'
  return 'danger'
}

function overallStatusLabel(status: string): string {
  if (status === 'healthy') return 'All Systems Operational'
  if (status === 'degraded') return 'Degraded - Some Components Unconfigured'
  return 'Unhealthy - Action Required'
}

// ============================================================================
// Category Helpers
// ============================================================================

const CATEGORY_ORDER: ComponentCategory[] = ['database', 'cache', 'storage', 'external', 'worker']

const CATEGORY_LABELS: Record<ComponentCategory, string> = {
  database: 'Databases',
  cache: 'Caches (KV)',
  storage: 'Object Storage',
  external: 'External Services',
  worker: 'Background Workers',
}

function groupByCategory(
  components: ComponentHealth[]
): { category: ComponentCategory; label: string; items: ComponentHealth[] }[] {
  const grouped = new Map<ComponentCategory, ComponentHealth[]>()
  for (const component of components) {
    const existing = grouped.get(component.category) ?? []
    existing.push(component)
    grouped.set(component.category, existing)
  }

  return CATEGORY_ORDER
    .filter((cat) => grouped.has(cat))
    .map((cat) => ({
      category: cat,
      label: CATEGORY_LABELS[cat],
      items: grouped.get(cat) ?? [],
    }))
}

function environmentBadgeClasses(env: string): string {
  if (env === 'production') return 'bg-red-50 text-red-700 ring-1 ring-red-600/20'
  if (env === 'staging') return 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20'
  return 'bg-gray-100 text-gray-600 ring-1 ring-gray-500/20'
}

// ============================================================================
// Component Health Card
// ============================================================================

function ComponentHealthCard({ component }: { component: ComponentHealth }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-gray-900">{component.component}</h3>
          <Badge variant={componentStatusVariant(component.status)}>
            {component.status}
          </Badge>
        </div>
        <p className="mt-0.5 text-xs text-gray-400">{component.description}</p>
        <p className="mt-1 text-sm text-gray-500">{component.message}</p>
        {component.details && component.details.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
            {component.details.map((entry) => (
              <span key={entry.label}>
                <span className="text-gray-500">{entry.label}:</span>{' '}
                {String(entry.value)}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="ml-4 text-right">
        <span className="text-xs text-gray-400">
          {component.latencyMs > 0 ? `${component.latencyMs}ms` : '-'}
        </span>
      </div>
    </div>
  )
}

// ============================================================================
// Test Email Card
// ============================================================================

function TestEmailCard({ activeProvider }: { activeProvider: EmailProviderName }) {
  const [email, setEmail] = useState('')
  const [provider, setProvider] = useState<EmailProviderName>(activeProvider)
  const [resultMessage, setResultMessage] = useState<string | null>(null)
  const [resultIsError, setResultIsError] = useState(false)
  const queryClient = useQueryClient()

  const testEmailMutation = useMutation({
    mutationFn: (data: { email: string; provider: EmailProviderName }) =>
      api.sendTestEmail(data),
    onSuccess: (data) => {
      setResultIsError(false)
      setResultMessage(
        data.messageId
          ? `Test email sent successfully (ID: ${data.messageId})`
          : 'Test email sent successfully'
      )
      // Refresh health so the active-provider badge reflects any change.
      void queryClient.invalidateQueries({ queryKey: ['infrastructure-health'] })
    },
    onError: (error: Error) => {
      setResultIsError(true)
      setResultMessage(error.message || 'Failed to send test email')
    },
  })

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value)
    },
    []
  )

  const handleProviderChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setProvider(e.target.value as EmailProviderName)
    },
    []
  )

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      const trimmed = email.trim()
      if (!trimmed) return
      setResultMessage(null)
      testEmailMutation.mutate({ email: trimmed, provider })
    },
    [email, provider, testEmailMutation]
  )

  return (
    <Card
      title="Test Email"
      description="Send a Test Email to verify email provider delivery"
    >
      <form onSubmit={handleSubmit} className="flex items-end gap-3">
        <div className="flex-1">
          <Input
            label="Recipient Email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={handleEmailChange}
          />
        </div>
        <Select
          name="provider"
          label="Provider"
          value={provider}
          onChange={handleProviderChange}
          options={EMAIL_PROVIDER_OPTIONS}
          className="w-44"
        />
        <Button
          type="submit"
          variant="primary"
          size="sm"
          isLoading={testEmailMutation.isPending}
        >
          Send Test Email
        </Button>
      </form>
      {resultMessage && (
        <div
          className={`mt-3 rounded-md p-3 text-sm ${
            resultIsError
              ? 'bg-red-50 text-red-700'
              : 'bg-green-50 text-green-700'
          }`}
        >
          {resultMessage}
        </div>
      )}
    </Card>
  )
}

// ============================================================================
// Active Email Provider Card
// ============================================================================

/**
 * Provider-selection form. Rendered only once the setting has loaded so the
 * dropdown can initialise from the effective provider without a sync effect.
 * The "Active provider" label reads from the live `setting` prop (which refetches
 * after a successful change); the dropdown tracks the admin's pending choice.
 */
function EmailProviderForm({ setting }: { setting: EmailProviderSetting }) {
  const [selected, setSelected] = useState<EmailProviderName>(setting.provider)
  const [resultMessage, setResultMessage] = useState<string | null>(null)
  const [resultIsError, setResultIsError] = useState(false)
  const queryClient = useQueryClient()

  const updateMutation = useMutation({
    mutationFn: (provider: EmailProviderName) =>
      api.updateEmailProviderSetting(provider),
    onSuccess: (data) => {
      setResultIsError(false)
      setResultMessage(
        `Active email provider set to ${EMAIL_PROVIDER_LABELS[data.provider]}`
      )
      // Refresh both the setting and the health badge that mirrors it.
      void queryClient.invalidateQueries({ queryKey: ['email-provider-setting'] })
      void queryClient.invalidateQueries({ queryKey: ['infrastructure-health'] })
    },
    onError: (error: Error) => {
      // Surface the API's config-gate message (e.g. "Cloudflare Email is not
      // configured") verbatim so the admin knows why the switch was refused.
      setResultIsError(true)
      setResultMessage(error.message || 'Failed to update email provider')
    },
  })

  const handleProviderChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelected(e.target.value as EmailProviderName)
    },
    []
  )

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      setResultMessage(null)
      updateMutation.mutate(selected)
    },
    [selected, updateMutation]
  )

  return (
    <>
      <div className="mb-4 flex items-center gap-2 text-sm">
        <span className="text-gray-500">Active provider:</span>
        <span className="font-medium text-gray-900">
          {EMAIL_PROVIDER_LABELS[setting.provider]}
        </span>
        <Badge variant={setting.isOverridden ? 'success' : 'gray'}>
          {setting.isOverridden ? 'Set in GUI' : 'Inherited from environment'}
        </Badge>
      </div>
      <form onSubmit={handleSubmit} className="flex items-end gap-3">
        <Select
          name="email-provider"
          label="Email Provider"
          value={selected}
          onChange={handleProviderChange}
          options={EMAIL_PROVIDER_OPTIONS}
          className="w-44"
        />
        <Button
          type="submit"
          variant="primary"
          size="sm"
          isLoading={updateMutation.isPending}
          disabled={selected === setting.provider && setting.isOverridden}
        >
          Save Provider
        </Button>
      </form>
      {resultMessage && (
        <div
          className={`mt-3 rounded-md p-3 text-sm ${
            resultIsError
              ? 'bg-red-50 text-red-700'
              : 'bg-green-50 text-green-700'
          }`}
        >
          {resultMessage}
        </div>
      )}
    </>
  )
}

function EmailProviderControl() {
  const { data: setting, isLoading } = useQuery({
    queryKey: ['email-provider-setting'],
    queryFn: () => api.getEmailProviderSetting(),
  })

  return (
    <Card
      title="Active Email Provider"
      description="Choose which provider delivers Spoofio email. Switching is blocked unless the target provider's credentials are configured."
    >
      {isLoading ? (
        <div className="h-10 w-44 animate-pulse rounded bg-gray-100" />
      ) : setting ? (
        <EmailProviderForm setting={setting} />
      ) : (
        <p className="text-sm text-gray-500">Failed to load email provider setting</p>
      )}
    </Card>
  )
}

// ============================================================================
// Reset Data Card
// ============================================================================

function ResetDataCard() {
  const [resultMessage, setResultMessage] = useState<string | null>(null)
  const [resultIsError, setResultIsError] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const queryClient = useQueryClient()

  const resetMutation = useMutation({
    mutationFn: () => api.resetData(),
    onSuccess: (data) => {
      setResultIsError(false)
      setResultMessage(
        `Reset complete: ${data.deleted.detections} detections, ${data.deleted.alerts} alerts, ${data.deleted.dedupKeys} dedup keys deleted`
      )
      setConfirming(false)
      void queryClient.invalidateQueries()
    },
    onError: (error: Error) => {
      setResultIsError(true)
      setResultMessage(error.message || 'Failed to reset data')
      setConfirming(false)
    },
  })

  return (
    <Card title="Reset Data" description="Delete all detections, alerts, and deduplication keys">
      {!confirming ? (
        <Button
          variant="danger"
          size="sm"
          onClick={() => { setConfirming(true); setResultMessage(null); }}
        >
          Reset All Data
        </Button>
      ) : (
        <div className="flex items-center gap-3">
          <span className="text-sm text-red-600 font-medium">Are you sure? This cannot be undone.</span>
          <Button
            variant="danger"
            size="sm"
            isLoading={resetMutation.isPending}
            onClick={() => { resetMutation.mutate(); }}
          >
            Yes, Reset
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => { setConfirming(false); }}
          >
            Cancel
          </Button>
        </div>
      )}
      {resultMessage && (
        <div
          className={`mt-3 rounded-md p-3 text-sm ${
            resultIsError
              ? 'bg-red-50 text-red-700'
              : 'bg-green-50 text-green-700'
          }`}
        >
          {resultMessage}
        </div>
      )}
    </Card>
  )
}

// ============================================================================
// Infrastructure Page
// ============================================================================

export function InfrastructurePage() {
  const {
    data: health,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['infrastructure-health'],
    queryFn: () => api.getInfrastructureHealth(),
  })

  const categoryGroups = useMemo(
    () => (health ? groupByCategory(health.components) : []),
    [health]
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Infrastructure Health</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor the status of Spoofio's infrastructure components
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => { void refetch() }}
          isLoading={isFetching}
        >
          Refresh
        </Button>
      </div>

      {/* Overall Status Banner */}
      {isLoading ? (
        <div className="animate-pulse rounded-lg bg-gray-100 p-4">
          <div className="h-6 w-48 rounded bg-gray-200" />
        </div>
      ) : health ? (
        <div
          className={`rounded-lg p-4 ${
            health.overall === 'healthy'
              ? 'bg-green-50 border border-green-200'
              : health.overall === 'degraded'
                ? 'bg-yellow-50 border border-yellow-200'
                : 'bg-red-50 border border-red-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Badge variant={overallStatusVariant(health.overall)}>
                {health.overall.toUpperCase()}
              </Badge>
              <span className="text-sm font-medium text-gray-900">
                {overallStatusLabel(health.overall)}
              </span>
              <span
                className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${environmentBadgeClasses(health.environment)}`}
                data-testid="environment-badge"
              >
                {health.environment}
              </span>
              <span
                className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-blue-600/20"
                data-testid="active-email-provider"
              >
                Email Provider: {EMAIL_PROVIDER_LABELS[health.activeEmailProvider]}
              </span>
            </div>
            <span className="text-xs text-gray-500">
              {formatAuditTimestamp(health.timestamp)}
            </span>
          </div>
        </div>
      ) : null}

      {/* Component Health Grid - Grouped by Category */}
      {isLoading ? (
        <Card title="Components" description="Individual component health status">
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse rounded-lg border border-gray-200 p-4">
                <div className="h-5 w-32 rounded bg-gray-200" />
                <div className="mt-2 h-4 w-48 rounded bg-gray-100" />
              </div>
            ))}
          </div>
        </Card>
      ) : health ? (
        <>
          {categoryGroups.map((group) => (
            <Card key={group.category} title={group.label}>
              <div className="space-y-3">
                {group.items.map((component) => (
                  <ComponentHealthCard key={component.component} component={component} />
                ))}
              </div>
            </Card>
          ))}
        </>
      ) : (
        <Card title="Components" description="Individual component health status">
          <p className="text-sm text-gray-500">Failed to load health data</p>
        </Card>
      )}

      {/* Active Email Provider */}
      <EmailProviderControl />

      {/* Test Email */}
      {health ? <TestEmailCard activeProvider={health.activeEmailProvider} /> : null}

      {/* Reset Data */}
      <ResetDataCard />
    </div>
  )
}
